class ProductSection extends HTMLElement {
    constructor() {
        super();

        this.productVariants = this.querySelector('#product-variants');
        this.productOptions = this.querySelectorAll('.product-options .variant-wrapper details summary');
        this.variantWrappers = this.productVariants.querySelectorAll('.variant-wrapper');
        this.radioBtns = this.productVariants.querySelectorAll('input[type="radio"]');
        this.variantFieldsets = this.productVariants.querySelectorAll('fieldset');
        this.masterVariantFields = this.querySelectorAll('.product-wrapper form[action*="/cart/add"] input[name="id"]');
        this.cartBtn = this.querySelectorAll('.product-wrapper form[action*="/cart/add"] button[type="submit"]');
        this.cartBtnText = this.querySelectorAll('.product-wrapper form[action*="/cart/add"] button[type="submit"] .btn-add-to-cart-text');

        this.productPrice = this.querySelector('.product-wrapper .js-product-price');
        this.productPriceButton = this.querySelectorAll('.btn-add-to-cart .js-product-price');
        this.productPriceText = this.querySelector('.product-wrapper .js-product-price .price');
        this.saveNow = this.querySelector('.product-wrapper .js-product-price .price-now');
        this.productCAP = this.querySelector('.product-wrapper .js-product-compare-at-price');
        this.saveIndication = this.querySelector('.product-wrapper .save-indication');
        this.saveAmountValue = this.querySelector('.product-wrapper .save-amount-value');
        this.saveAmountPercentage = this.querySelector('.product-wrapper .save-amount-percentage');
        this.selectedMattressUpsell = this.querySelector('.selected-mattress-upsell');
        this.selectedHeadboardUpsell = this.querySelector('.selected-headboard-upsell');

        this.isProductPage = document.body.classList.contains('template-product') ? true : false;
        this.isSingleVariant = this.querySelector('.single-variant') ? true : false;

        this.tooltips = this.querySelectorAll('.colour-tooltip');
        this.colourRadios = this.querySelectorAll(".product-colour-option-wrapper input[name='Colour']");

        this.detailElements = this.querySelectorAll('.product-options .variant-wrapper details, .product-options .product-colour-option-wrapper details');

        // Cache DOM queries for upsell popups
        this.upsellPopups = null;

        // Cache parsed variant JSON (cleared if section DOM is replaced)
        this._variantData = null;

        // Debounce variant updates (timer; does not need rAF)
        this._variantUpdateTimeoutId = null;

        // Debounce timeout for URL updates
        this._urlUpdateTimeout = null;
    }

    connectedCallback() {
        this.initListeners();
    }

    initListeners() {
        this.productVariants.addEventListener('change', this.handleVariantChange.bind(this));

        if (!this.isSingleVariant) {
            this.updateProductVariants();
        }

        this.productOptions.forEach(option => option.addEventListener('click', this.handleOptionClick.bind(this)));

        this.tooltips.forEach((tooltip) => {
            const parent = tooltip.closest('.accordion-colour-swatch');

            parent.addEventListener('mouseenter', this.checkAlignment.bind(this, parent, tooltip));
            parent.addEventListener('focus', this.checkAlignment.bind(this, parent, tooltip));
        });

        // Add change event listener to each radio button
        this.colourRadios.forEach((radio) => {
            radio.addEventListener('change', (e) => {
                if (e.target.checked) {
                    this.updateColourProperties(e.target.value);
                    this.updateSelectedColour(e.target.value, e.target.dataset.value);
                }
            });
        });

        this.detailElements.forEach(detail => {
            detail.addEventListener('click', this.handleDetailClick.bind(this));
        });
    }

    handleDetailClick(e) {
        const detail = e.target.closest('details');

        if (!detail.hasAttribute('open')) {
            this.detailElements.forEach(otherDetail => {
                if (otherDetail !== detail) {
                    otherDetail.removeAttribute('open');
                }
            });
        }
    }

    // Function to update inputs with class 'colour-property'
    updateColourProperties(value) {
        // Batch DOM reads - get all elements first
        const colourProperties = this.querySelectorAll('.colour-property');
        // Batch DOM writes - update all at once to avoid multiple reflows
        for (let i = 0; i < colourProperties.length; i++) {
            colourProperties[i].value = value;
        }
    }

    updateSelectedColour(value, className) {
        // Batch DOM reads - get wrapper once, then query children
        const wrapper = this.querySelector('.product-colour-option-wrapper');
        if (!wrapper) return;

        const selectedOption = wrapper.querySelector('.selected-option');
        const selectedIcon = wrapper.querySelector('.selected-icon');

        if (!selectedOption || !selectedIcon) return;

        // Batch DOM writes - use single className assignment instead of multiple operations
        // This avoids multiple style recalculations
        selectedOption.textContent = value;
        selectedIcon.className = 'selected-icon ' + className;
    }

    checkAlignment(parent, tooltip) {
        const parentRect = parent.getBoundingClientRect();
        const tooltipWidth = tooltip.offsetWidth;

        // Reset alignment first
        tooltip.classList.remove('align-right');

        // If tooltip overflows right, align it to the left
        if (parentRect.left + tooltipWidth > window.innerWidth) {
            tooltip.classList.add('align-right');
        }
    }

    handleOptionClick(e) {
        const currentWrapper = e.target.closest('.product-option-wrapper');

        // Close all other open options that have the 'open' attribute
        this.productOptions.forEach(otherOption => {
            const otherWrapper = otherOption.closest('.product-option-wrapper');
            const otherDetails = otherWrapper.querySelector('details');

            // Skip the currently clicked option
            if (otherWrapper !== currentWrapper && otherDetails.hasAttribute('open')) {
                otherDetails.removeAttribute('open');
            }
        });
    }

    handleVariantChange(e) {
        if (e.target.type === 'radio') {
            const fieldset = e.target.closest('.product-option-wrapper');
            const summary = fieldset.querySelector('summary');
            const selectedIcon = summary.querySelector('.selected-icon');
            const selectedOption = summary.querySelector('.selected-option');
            const selectedLabel = e.target.nextElementSibling;

            // Update selected option text
            if (selectedOption) {
                const optionText = selectedLabel.querySelector('.variant-label');
                if (optionText) {
                    selectedOption.textContent = optionText.textContent;
                }
            }

            // Update selected icon
            if (selectedIcon) {
                const labelImg = selectedLabel.querySelector('img');
                if (labelImg) {
                    // Clear existing content
                    selectedIcon.innerHTML = '';
                    // Clone the image from the selected label
                    const clonedImg = labelImg.cloneNode(true);
                    selectedIcon.appendChild(clonedImg);
                } else {
                    // If no image, clear the selected icon
                    selectedIcon.innerHTML = '';
                }
            }
        }
    }

    /**
     * Gets variant data from the JSON script tag (cached after first parse)
     * @returns {Array} Array of variant objects
     */
    getVariantData() {
        if (this._variantData != null) {
            return this._variantData;
        }
        const el = this.productVariants?.querySelector('[type="application/json"]');
        if (!el) return [];
        this._variantData = JSON.parse(el.textContent);
        return this._variantData;
    }

    /**
     * Updates the add to cart button state based on variant availability
     * @param {boolean} available - Whether the current variant is available
     * @param {boolean} [sync] - If true, update immediately; otherwise defer with rAF (for non-variant callers)
     */
    updateAddToCartButton(available, sync) {
        const run = () => {
            if (available) {
                this.cartBtn.forEach(btn => {
                    btn.classList.add('btn-primary');
                    btn.disabled = false;
                });
                this.cartBtnText.forEach(text => {
                    text.innerHTML = window.custom_product.addToCart;
                });
            } else {
                this.cartBtn.forEach(btn => {
                    btn.classList.remove('btn-primary');
                    btn.disabled = true;
                });
                this.cartBtnText.forEach(text => {
                    text.innerHTML = window.custom_product.soldOut;
                });
            }
        };
        if (sync) {
            run();
        } else {
            requestAnimationFrame(run);
        }
    }

    /**
     * Updates product pricing display including sale prices and savings
     * @param {Object|number} variantOrId - The selected variant object, or variant ID (legacy)
     * @param {boolean} [sync] - If true, update immediately (used from variant change path)
     */
    updateVariantPrice(variantOrId, sync) {
        const variantData = typeof variantOrId === 'object' ? variantOrId : this.getVariantData().find(v => v.id === variantOrId);
        if (!variantData) return;

        const run = () => {
            this.productPriceText.innerHTML = formatMoney(variantData.price, window.theme.moneyFormat);
            this.productPriceText.dataset.price = variantData.price;

            let total = variantData.price;
            if (this.selectedHeadboardUpsell && !this.selectedHeadboardUpsell.classList.contains('hidden')) {
                const headboardPrice = this.selectedHeadboardUpsell.getAttribute('data-price');
                const headboardQuantity = this.selectedHeadboardUpsell.getAttribute('data-variant-quantity');
                if (headboardPrice != null && headboardQuantity != null) {
                    total += Number(headboardPrice) * Number(headboardQuantity);
                }
            }
            this.productPriceButton.forEach(btn => {
                btn.innerHTML = formatMoney(total, window.theme.moneyFormat);
            });

            if (variantData.compare_at_price != null) {
                this.productPrice.classList.add('sale');
                this.productCAP.classList.remove('hidden');
                this.productCAP.innerHTML = formatMoney(variantData.compare_at_price, window.theme.moneyFormat);
                this.saveNow.classList.remove('hidden');
                this.saveIndication.classList.remove('hidden');
                this.saveAmountValue.innerHTML = formatMoney(variantData.compare_at_price - variantData.price, window.theme.moneyFormat);
                this.saveAmountPercentage.innerHTML = `${Math.round((variantData.compare_at_price - variantData.price) / variantData.compare_at_price * 100)}%`;
            } else {
                this.productCAP.classList.add('hidden');
                this.saveIndication.classList.add('hidden');
                this.productPrice.classList.remove('sale');
                this.saveNow.classList.add('hidden');
            }
        };
        if (sync) {
            run();
        } else {
            requestAnimationFrame(run);
        }
    }

    /**
    * Updates all product details for the selected variant
    * @param {Object} variant - The selected variant object
    */
    updateFinalVariant(variant) {
        this.masterVariantFields.forEach(field => {
            field.value = variant.id;
        });
        this.updateVariantPrice(variant, true);
        this.updateAddToCartButton(variant.available, true);

        // Sync Intelligems data-variant-id for main product price elements
        this.querySelectorAll('.product-price-inner[data-variant-id], .js-product-price[data-variant-id], .product-prices-wrapper[data-variant-id], .price[data-variant-id], .js-product-compare-at-price[data-variant-id], .save-amount-value[data-variant-id], .save-amount-percentage[data-variant-id]').forEach(el => {
            el.dataset.variantId = String(variant.id);
        });

        // Update URL with variant parameter (debounced)
        if (this.isProductPage && !this.closest('.product-quick-view-content')) {
            this.debouncedUrlUpdate(variant.id);

            const variantSize = variant.option1;
            // Use cached upsell popups
            if (!this.upsellPopups) {
                this.upsellPopups = this.querySelectorAll('.product-upsell-popup');
            }
            const upsellPopups = this.upsellPopups;

            upsellPopups.forEach(popup => {
                const variantSelect = popup.querySelector('.upsell-select');

                if (variantSelect) {
                    let firstMatchFound = false;

                    Array.from(variantSelect.options).forEach((option) => {
                        if (option.text.toLowerCase().includes(variantSize.toLowerCase())) {
                            if (!firstMatchFound) {
                                option.selected = true;
                                variantSelect.dispatchEvent(new Event('change'));
                                firstMatchFound = true;
                            }
                        }
                    });
                }
            });

            let dimensionSections = document.querySelectorAll('dimensions-section');

            dimensionSections.forEach(section => {
                section.dispatchEvent(new CustomEvent('dimension:variant:changed', {
                    detail: {
                        variant: variant
                    }
                }));
            });
        }
    };

    /**
     * Resolves one variant from selected option values (single pass, early exit).
     * @param {Array} allVariants - Full variant list
     * @param {Array} selectedOptions - Current option values
     * @returns {Object} Matched variant
     */
    findVariantForOptions(allVariants, selectedOptions) {
        let v = allVariants.find(variant =>
            selectedOptions.every((selectedValue, index) => {
                const key = `option${index + 1}`;
                return variant[key] === selectedValue;
            })
        );
        if (v) return v;
        v = allVariants.find(variant =>
            selectedOptions.every((selectedValue, index) => {
                const key = `option${index + 1}`;
                return variant[key] && variant[key].includes(selectedValue);
            })
        );
        if (v) return v;
        if (selectedOptions.length === 1) {
            v = allVariants.find(variant => variant.title === selectedOptions[0]);
            if (v) return v;
            v = allVariants.find(variant => variant.option1 === selectedOptions[0]);
            if (v) return v;
        }
        for (let i = 0; i < selectedOptions.length; i++) {
            const key = `option${i + 1}`;
            v = allVariants.find(variant => variant[key] === selectedOptions[i]);
            if (v) return v;
        }
        return allVariants.find(variant => variant.available) || allVariants[0];
    }

    /**
    * Filters variants based on selected options using partial matching
    * @param {Array} selectedOptions - Array of selected option values
    * @param {Array} [allVariants] - Pre-fetched variant array (avoids getVariantData when provided)
    * @returns {Array} Filtered variants that match the selected options
    */
    getAvailableOptionsForCombination(selectedOptions, allVariants) {
        let filteredVariants = allVariants != null ? allVariants : this.getVariantData();

        selectedOptions.forEach((selectedValue, index) => {
            if (selectedValue) {
                const optionKey = `option${index + 1}`;
                filteredVariants = filteredVariants.filter(v =>
                    v[optionKey] && v[optionKey].includes(selectedValue)
                );
            }
        });

        return filteredVariants;
    };

    /**
     * Updates the availability of variant options based on current selections
     * @param {NodeList} checkedBtns - Currently selected radio buttons
     * @param {Array} [allVariants] - Pre-fetched variant array (avoids getVariantData when provided)
     */
    updateRemainingOptions(checkedBtns, allVariants) {
        // Clear all unavailable states first
        this.productVariants.querySelectorAll('.js-unavailable-variant').forEach(label => label.classList.remove('js-unavailable-variant'));

        const variants = allVariants != null ? allVariants : this.getVariantData();

        // Handle single-option products (simplified logic)
        if (this.variantFieldsets.length === 1) {
            variants.forEach(variant => {
                if (!variant.available) {
                    const label = this.productVariants.querySelector(`.js-variant-label[data-label-value="${variant.option1}"]`);

                    label && label.classList.add('js-unavailable-variant');
                }
            });
            return;
        }

        // Get current selections as an array
        const selectedOptions = Array.from(checkedBtns).map(btn => btn.value);

        // For each fieldset, determine which options should be available
        this.variantFieldsets.forEach((fieldset, fieldsetIndex) => {
            const fieldsetLabels = fieldset.querySelectorAll('.js-variant-label');
            const allOptionsForThisFieldset = Array.from(fieldsetLabels).map(label => label.getAttribute('data-label-value'));

            // Check availability for each option in this fieldset
            allOptionsForThisFieldset.forEach(optionValue => {
                const testOptions = [...selectedOptions];
                testOptions[fieldsetIndex] = optionValue;
                const variantsWithThisOption = this.getAvailableOptionsForCombination(testOptions, variants);
                const isAvailable = variantsWithThisOption.length > 0;

                // Mark as unavailable if no variants exist for this combination
                if (!isAvailable) {
                    const label = fieldset.querySelector(`.js-variant-label[data-label-value="${optionValue}"]`);

                    label && label.classList.add('js-unavailable-variant');
                }
            });
        });
    };

    /**
    * Main function to initialize and handle product variant selection
    * Supports single-option, two-option, and three-option products
    * Handles dynamic availability updates and price changes
    */
    updateProductVariants() {

        /**
         * Handles variant selection. Debounced with setTimeout (no rAF); coalesces rapid clicks.
         */
        this.radioBtns.forEach(radio => radio.addEventListener('change', () => {
            if (this._variantUpdateTimeoutId != null) {
                clearTimeout(this._variantUpdateTimeoutId);
            }
            this._variantUpdateTimeoutId = setTimeout(() => {
                this._variantUpdateTimeoutId = null;

                const radioBtnsChecked = this.productVariants.querySelectorAll('input[type="radio"]:checked');
                const allVariants = this.getVariantData();
                const selectedOptions = Array.from(radioBtnsChecked).map(btn => btn.value);

                const combineVariant = this.findVariantForOptions(allVariants, selectedOptions);

                const sizeRadio = this.variantFieldsets[0]?.querySelector('input[type="radio"]:checked');
                if (sizeRadio && sizeRadio.name === 'Size') {
                    const cachedFirstPart = sizeRadio.value.split('-')[0].toLowerCase().trim();
                    const cachedUpsellSelects = this.querySelectorAll('.upsell-select');
                    const cachedSelectOptions = Array.from(cachedUpsellSelects).map(select =>
                        Array.from(select.querySelectorAll('option'))
                    );
                    cachedUpsellSelects.forEach((select, index) => {
                        const selectOptions = cachedSelectOptions[index];
                        for (const option of selectOptions) {
                            if (option.text.toLowerCase().trim().includes(cachedFirstPart)) {
                                option.selected = true;
                                select.value = option.value;
                                select.dispatchEvent(new Event('change'));
                                break;
                            }
                        }
                    });
                }

                this.updateFinalVariant(combineVariant);
                this.updateRemainingOptions(radioBtnsChecked, allVariants);
            }, 0);
        }));

        const currentRadioBtnsChecked = this.productVariants.querySelectorAll('input[type="radio"]:checked');
        this.updateRemainingOptions(currentRadioBtnsChecked);

        const variantSize = this.querySelector('.product-option-wrapper[data-fieldset-position="1"] .selected-option').textContent;
        // Use cached upsell popups
        if (!this.upsellPopups) {
            this.upsellPopups = this.querySelectorAll('.product-upsell-popup');
        }
        const upsellPopups = this.upsellPopups;

        upsellPopups.forEach(popup => {
            const variantSelect = popup.querySelector('.upsell-select');

            if (variantSelect) {
                let firstMatchFound = false;

                Array.from(variantSelect.options).forEach((option) => {
                    if (option.text.toLowerCase().includes(variantSize.toLowerCase())) {
                        if (!firstMatchFound) {
                            option.selected = true;
                            variantSelect.dispatchEvent(new Event('change'));
                            firstMatchFound = true;
                        }
                    }
                });
            }
        });
    }

    /**
     * Debounced URL update to prevent rapid-fire history changes
     * @param {number} variantId - The variant ID to update in URL
     */
    debouncedUrlUpdate(variantId) {
        if (this._urlUpdateTimeout) {
            clearTimeout(this._urlUpdateTimeout);
        }
        
        this._urlUpdateTimeout = setTimeout(() => {
            const currentURLWithoutParams = window.location.href.split('?')[0];
            history.replaceState(null, null, `${currentURLWithoutParams}?variant=${variantId}`);
            this._urlUpdateTimeout = null;
        }, 300); // 300ms debounce
    }
}

customElements.define('product-section', ProductSection);